import React, { useContext, useMemo } from 'react';

import Loader from '@components/shared/Loader/Loader';
import ResultsResponsive from '@components/shared/ResultsResponsive/ResultsResponsive';
import topicPageLinks from '@constants/topicPageLinks';
import _ from 'lodash';
import {
  ITemplateItem,
  ITemplateItemProps,
  ResponsiveRowPattern,
} from '@components/shared/ResultsResponsive/ResponsiveTemplate';
import {
  ResponsiveTemplateMobile1,
  ResponsiveTemplateMobile2,
  ResponsiveTemplateMobile3,
  ResponsiveTemplateMobile4,
  ResponsiveTemplateMobile6,
} from '@components/shared/ResultsResponsive/ResponsiveTemplates/Mobile';
import {
  ResponsiveTemplateDesktop1,
  ResponsiveTemplateDesktop2,
  ResponsiveTemplateDesktop3,
  ResponsiveTemplateDesktop4,
  ResponsiveTemplateDesktop5
} from '@components/shared/ResultsResponsive//ResponsiveTemplates/Desktop';
import DiscoverBoardCard from '@components/shared/DiscoverCard/DiscoverBoardCard';
import TopicLinksCard from '@components/shared/TopicLinksCard/TopicLinksCard';
import { isSavedBoard } from '@lib/board/isSavedBoard';
import UserContext from '@context/User/user-context';
import useHolidaySale from '@hooks/useHolidaySale/useHolidaySale';
import FindYourStyle from '@components/shared/CTACards/FindYourStyle';
import WantThisLook from '@components/shared/CTACards/WantThisLook';
import HolidaySaleCTA from '@components/shared/CTACards/HolidaySaleCTA';
import BlogPostCard from '@components/shared/BlogPostCard/BlogPostCard';
import ConversionEmailHarvesterCard from '@components/shared/ConversionEmailHarvester/DiscoverFeedCard/ConversionEmailHarvesterCard';
import DiscoverBestSellers from '@components/shared/CTACards/DiscoverBestSellers';
import ShopBanner from '@components/pages/ExploreFeed/components/ShopBanner/ShopBanner';
import styles from './styles.module.scss';

interface IExploreResultsProps {
  discoverSlug?: string;
  readOnly: boolean;
  onSaveAsset: (sourceBoard: any, asset: any, listId?: string) => any;
  savedAssets: any;
  boards: any[];
  posts: any[];
  loading: boolean;
}

export default React.memo(Results, (prev, next) => {
  return (prev.boards === next.boards)
    && (prev.savedAssets === next.savedAssets)
    && (prev.loading === next.loading);
});

function Results({
  discoverSlug,
  onSaveAsset,
  readOnly,
  savedAssets,
  boards,
  posts,
  loading,
}: IExploreResultsProps) {
  const { user } = useContext(UserContext);

  /**
   * @param {string} typename
   * @param {object} item
   */
  const handleOnSave = (typename: string, item: any) => {
    if (typename === 'BoardModel') {
      const sourceBoard = item;
      const mainBoardAsset = item.mainBoardAsset?.asset;

      if (mainBoardAsset) {
        return onSaveAsset(sourceBoard, mainBoardAsset);
      }
    }
    return undefined;
  };

  const topicLinks: any = useMemo(() => {
    return _.shuffle(
      topicPageLinks.filter(
        (topic) => !topic.omitFromFeed && topic.slug !== discoverSlug,
      ),
    );
  }, [boards[0]?.id]);

  const holidaySale = useHolidaySale();

  const itemVisitorProvider = () => {
    let boardIndex = 0;
    let postIndex = 0;
    let topicLinkIndex = 0;
    let ctaIndex = 0;
    let bannerIndex = 0;
    return ({ type, size }: ITemplateItemProps): ITemplateItem | undefined => {
      switch (type) {
        case 'Board':
          if (boardIndex < boards.length) {
            // eslint-disable-next-line no-plusplus
            const board = boards[boardIndex++];
            return {
              item: board,
              component: (
                <DiscoverBoardCard
                  board={board}
                  saved={isSavedBoard(board, savedAssets)}
                  readOnly={readOnly}
                  size={size}
                  onSave={handleOnSave}
                />
              ),
            };
          }
          break;

        case 'TopicLink':
          if (topicLinks && topicLinkIndex < topicLinks?.length) {
            const cardLinks = topicLinks.slice(topicLinkIndex, topicLinkIndex + 3);
            topicLinkIndex += 3;
            return {
              item: { id: cardLinks[0].slug },
              component: (
                <TopicLinksCard topics={cardLinks} size={ size } />
              ),
            };
          }
          break;

        case 'BlogPost':
          if (posts[postIndex]) {
            const currentPost = posts[postIndex];
            return {
              // eslint-disable-next-line no-plusplus
              item: { id: `bp${postIndex++}` },
              component: (
                <BlogPostCard
                  title={currentPost.title}
                  url={currentPost.url}
                  imageUrl={currentPost.largeImage}
                />
              )
            };
          }
          break;

        case 'ConversionEmailHarvester': {
          return {
            item: { id: `ceh1` },
            component: (
              <ConversionEmailHarvesterCard insploreTopic={discoverSlug} />
            )
          };
        }

        case 'CTA':
          if (holidaySale?.isRoomPackageSale()) {
            return {
              item: { id: `cta${ctaIndex}` },
              component: (
                <HolidaySaleCTA size={size} sale={holidaySale} />
              ),
            };
          } else if (user) {
            ctaIndex += 1;
            if (ctaIndex % 2) {
              return {
                item: { id: `cta${ctaIndex}` },
                component: (
                  <WantThisLook size={size} />
                ),
              };
            } else {
              return {
                item: { id: `cta${ctaIndex}` },
                component: (
                  <DiscoverBestSellers size={size} />
                ),
              };
            }
          } else { // not logged in
            ctaIndex += 1;
            if (ctaIndex % 2) {
              return {
                item: { id: `cta${ctaIndex}` },
                component: (
                  <FindYourStyle size={size} />
                ),
              };
            } else {
              return {
                item: { id: `cta${ctaIndex}` },
                component: (
                  <DiscoverBestSellers size={size} />
                ),
              };
            }
          }

        case 'Banner':
          bannerIndex += 1;
          return {
            item: { id: `banner${bannerIndex}` },
            component: (
              <ShopBanner />
            ),
          };

        default:
          break;
      }
      return undefined;
    };
  };

  const getDesktopResponsiveRowPattern = () => {
    if (user || holidaySale?.isActive()) {
      return new ResponsiveRowPattern([
        ResponsiveTemplateDesktop1,
        ResponsiveTemplateDesktop2,
        ResponsiveTemplateDesktop5,
        ResponsiveTemplateDesktop3,
      ], { holidaySale, user });
    }

    return new ResponsiveRowPattern([
      ResponsiveTemplateDesktop4,
      ResponsiveTemplateDesktop2,
      ResponsiveTemplateDesktop5,
      ResponsiveTemplateDesktop1,
      ResponsiveTemplateDesktop3,
    ], { holidaySale, user });
  };

  const getMobileResponsiveRowPattern = () => {
    if (holidaySale?.isActive()) {
      return new ResponsiveRowPattern([
        ResponsiveTemplateMobile1,
        ResponsiveTemplateMobile2,
        ResponsiveTemplateMobile1,
        ResponsiveTemplateMobile3,
      ], { holidaySale, user });
    }

    return new ResponsiveRowPattern([
      ResponsiveTemplateMobile1,
      ResponsiveTemplateMobile2,
      ResponsiveTemplateMobile6,
      ResponsiveTemplateMobile4,
      ResponsiveTemplateMobile1,
      ResponsiveTemplateMobile3,
    ], { holidaySale, user });
  };

  return (
    <div className={ styles.Results }>
      <ul className={ styles.ResultList }>
        <ResultsResponsive
          itemVisitorProvider={ itemVisitorProvider }
          desktopPattern={getDesktopResponsiveRowPattern()}
          mobilePattern={getMobileResponsiveRowPattern()}
        />
      </ul>

      <div className={ styles.LoadingWrap }>
        { loading && (<Loader />)}
      </div>
    </div>
  );
}
